import { useEffect, useState } from 'react';
import { Button } from '@meitu/saas-ui';
import { base64Kit } from '@meitu/saas-editor-core/es/utils/base64-kit';
import { readFileMd5 } from '@meitu/saas-editor-core/es/utils/file-kit';
import { isEmptyEffect } from '@meitu/saas-editor-core/es/utils/layer-kit';
import { traverseTree, traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import {
    IExtraInfo,
    IImageLayerProps,
    ILayerProps,
    ITextLayerProps,
    LayerType,
} from '@meitu/saas-editor-core/es/version-types';
import { CollapseType } from '@meitu/saas-editor-ui';
import {
    IParsePsdToClientTemplateResult,
    IStage,
    PsdParseModal,
    setFileParserEditorFonts,
} from '@meitu/x-design-file-parser';
import { uploadFileToObs } from '@meitu/xdesign-widgets';
import { resetDuplicateLayerIds } from '@/hooks/fetch-template/reset-duplicate-layer-ids';
import { adjustFontFamily } from '@/hooks/fetch-template/utils';
import { editor } from '@/instance';
import { useCollapseModel } from '@/store/collapse-models/collapse-model';
import { getFontMaterialModel } from '@/store/models/font-material-model';
import { renderImageEffectToUrl } from '@/utils/render-image-effect';

const UploadPsd = () => {
    const [disableOperate, setDisableOperate] = useState(false);
    const setCollapseType = useCollapseModel((state) => state.setCurrentType);

    useEffect(() => {
        // 监听editor状态
        editor.onState(({ disableOperate, disablePick, isBaseOperate }) => {
            setDisableOperate((disableOperate || disablePick) && !isBaseOperate);
        });
    }, []);

    const createTemplateByPsd = async (data: IParsePsdToClientTemplateResult | null) => {
        if (!data) {
            return;
        }

        const { stages } = data;

        const fontsRequired: string[] = [];

        const templateConf = [] as any;

        stages.forEach((stage: IStage) => {
            const { clientLayers, stageWidth, stageHeight, name } = stage;

            const layers = clientLayers.map((layer: any) => {
                traverseTree(layer, (child: ILayerProps) => {
                    if (child.layerType === LayerType.TEXT_LAYER) {
                        const { textContents, fontFamily } = child.data as ITextLayerProps;

                        fontsRequired.push(fontFamily);

                        if (textContents && textContents.length > 0) {
                            for (let i = 0; i < textContents.length; i++) {
                                fontsRequired.push(textContents[i].fontFamily);
                            }
                        }

                        if (!(child as any).__runtime) {
                            (child as any).__runtime = {};
                        }
                        (child as any).__runtime.isInit = true;
                    }
                });

                return layer;
            });

            templateConf.push({
                width: stageWidth,
                height: stageHeight,
                name,
                layers,
                settingType: null,
            });
        });

        const extraInfos: IExtraInfo[] = [];
        templateConf.forEach((element: any) => {
            const { settingType, width, height } = element;
            extraInfos.push({ settingType, width, height });
        });
        const importedStages: ILayerProps[][] = templateConf.map((item: { layers: ILayerProps[] }) => item.layers);

        // Canvas 内核以全局 ID 存图层；插入前保留已占用 ID，并修复本次导入的重复 ID。
        const usedLayerIds = new Set<string>();
        traverseTrees(editor.services.frame.getFrames().flat(), (layer: ILayerProps) => {
            usedLayerIds.add(layer.id);
        });
        const duplicateIdLayers: ILayerProps[] = [];
        importedStages.forEach((layers) => {
            traverseTrees(layers, (layer: ILayerProps) => {
                if (usedLayerIds.has(layer.id)) {
                    duplicateIdLayers.push(layer);
                } else {
                    usedLayerIds.add(layer.id);
                }
            });
        });
        resetDuplicateLayerIds(duplicateIdLayers, usedLayerIds);

        // 添加画布的额外信息
        editor.services.frame.delActiveLayer();
        await editor.services.stage.insertWithoutHistory(importedStages, extraInfos);

        const effectAllLayers = [] as ILayerProps[];
        for (let i = 0; i < templateConf.length; i++) {
            traverseTrees(templateConf[i].layers, (layer: ILayerProps) => {
                if (layer.layerType === LayerType.IMAGE_LAYER && !isEmptyEffect(layer.data as IImageLayerProps)) {
                    effectAllLayers.push(layer);
                }
            });
        }

        for (let i = 0; i < effectAllLayers.length; i++) {
            const url = await renderImageEffectToUrl(effectAllLayers[i]);

            ((effectAllLayers[i].data as IImageLayerProps).effect as any).screenshotUrl =
                await base64Kit.getRemoteUrlAsync(url || '');

            // 导入特效截图时不激活图片，否则第二页图层会显示在第一页的选框与属性面板中。
            await editor.imageLayer.updateLayer({ ...effectAllLayers[i] }, undefined, false, undefined, {
                activateLayer: false,
            });
        }

        setCollapseType(CollapseType.NAVIGATOR_MENU);
    };

    const uploadPsd = async () => {
        // PSD 缺失字体会替换为默认字体，解析器也需要 app_src 范围外补齐的回退字体。
        const { fonts: fontMaterials, fallbackFonts } = getFontMaterialModel();
        await setFileParserEditorFonts([...fontMaterials, ...fallbackFonts]);
        const fonts = editor.services.font.getFonts();
        const fileMap = {} as any;
        PsdParseModal.show({
            showCloseButton: true,
            uploadFileToOBS: async (file: File, extension?: string) => {
                const md5 = await readFileMd5(file);

                if (fileMap[md5]) {
                    return fileMap[md5];
                }
                const result = await uploadFileToObs(file);

                fileMap[md5] = result.url;
                return result.url;
            },
            finishCallback: async (data: any | null) => {
                if (data.file) {
                    createTemplateByPsd({
                        psdChild: [],
                        psdFile: null as any,
                        name: data.name || '',
                        resolution: data.resolution,
                        stages: data.templateConf.map((item: any) => {
                            adjustFontFamily(item.layers, fonts);

                            return {
                                clientLayers: item.layers,
                                preview: item.preview || '',
                                stageWidth: item.width,
                                stageHeight: item.height,
                                replaceFonts: [],
                                name: item.name || '',
                                settingType: item.settingType,
                            };
                        }),
                    });
                } else {
                    createTemplateByPsd(data);
                }
            },
            title: '文件上传解析',
            helpDocUrl: 'https://www.yuque.com/meitumcp/vvtts5/yrz632?from_wecom=1',

            noParseBgLayer: false,
        });
    };

    return (
        <Button type="primary" style={{ marginRight: '10px' }} data-blue disabled={disableOperate} onClick={uploadPsd}>
            插入psd
        </Button>
    );
};

export default UploadPsd;
